import { useState } from "react";

import { useTranslation } from "react-i18next";

import { Button, Divider, Icon, Message, Segment } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import type { ItemRefs } from "components/ideas/useIdeaScrollTracking";

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor";
import { StageSegment } from "../../ImpactStatements/StageSegments";
import { useImpactStageMap } from "../../ImpactStatements/statementUtils";
import { useImpactStatements } from "../../ImpactStatements/useImpactStatements";

type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];

type ImpactStatementManagementProps = {
  ideaId: string;
  sectionRefs?: React.RefObject<ItemRefs>;
  scrollRef?: React.RefObject<HTMLDivElement>;
};

const ImpactStatementManagement = ({
  ideaId,
}: ImpactStatementManagementProps) => {
  const { t } = useTranslation();
  const segmentMap = useImpactStageMap();
  const [currentImpactStage, setCurrentImpactStage] = useState<
    NonNullable<ImpactStatements["userEstimate"]>["stage"] | null
  >(null);
  const { statementDefinition, impactStatements, canEditProjectStages } =
    useImpactStatements(ideaId);

  if (currentImpactStage) {
    return (
      <div>
        <Button basic compact onClick={() => setCurrentImpactStage(null)}>
          <Icon name="arrow left" />
          Back to overview
        </Button>

        <Divider />
        <ImpactStatementEditor
          ideaId={ideaId}
          forStage={currentImpactStage}
          restrictStagesTo={[currentImpactStage]}
          editable={
            currentImpactStage !== "userEstimate" && canEditProjectStages
          }
        />

        <Divider hidden />
        <Button secondary onClick={() => setCurrentImpactStage(null)}>
          <Icon name="check" />
          Done
        </Button>
      </div>
    );
  }

  return (
    <div>
      {statementDefinition?.showOnIdea ?
        impactStatements?.userEstimate ?
          <StageSegment
            ideaId={ideaId}
            stage="userEstimate"
            onView={() => {
              setCurrentImpactStage("userEstimate");
            }}
          />
        : <Segment secondary>
            <div>
              <div>
                <h3>
                  <Icon name={segmentMap.userEstimate.icon} />{" "}
                  {segmentMap.userEstimate.name}
                </h3>
                {segmentMap.userEstimate.description}
              </div>
              <Message>
                The {t("generic.idea")} author did not create an estimate.
              </Message>
            </div>
          </Segment>

      : null}
      <StageSegment
        ideaId={ideaId}
        stage="projectEstimate"
        onView={() => {
          setCurrentImpactStage("projectEstimate");
        }}
        onCreated={() => {
          setCurrentImpactStage("projectEstimate");
        }}
      />
      <StageSegment
        ideaId={ideaId}
        stage="finalOutcome"
        onView={() => {
          setCurrentImpactStage("finalOutcome");
        }}
        onCreated={() => {
          setCurrentImpactStage("finalOutcome");
        }}
      />
    </div>
  );
};

export default ImpactStatementManagement;
